import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { DebounceInput } from 'react-debounce-input';
import TimeInput from '../../../../components/TimeInput/TimeInput';
import Highlighter from 'react-highlight-words';
import styles from './SubtitlesItem.module.scss';
import SubtitleActions from '../SubtitleActions/SubtitleActions';
import Icon from '../../../../components/Icon/Icon';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../../../components/Tooltip/Tooltip';
import { TIME_CORRECTION_STEP, VOICEOVER_COVERAGE_TYPES } from '../../const';
import { checkForFullCoverage } from '../../utils';
import { CustomButton } from '../../../../components/Button/Button';
import { ENTER_KEY_CODE, ESCAPE_KEY_CODE } from '../../../../constants/common';

const MAX_ITEM_LENGTH = 125;
const MAX_TRANSLATED_ITEM_LENGTH = 500;
const TEXTAREA_START_HEIGHT = 28;

const LONG_LINE_CHARACTERS = 45;

const SubtitlesItem = ({
  originalData,
  data,
  index,
  id,
  active,
  isTranslation,
  updateText,
  updateStartStamp,
  updateEndStamp,
  addSubtitle,
  mergeSubtitle,
  deleteSubtitle,
  searchValue,
  disableAutoScroll,
  handleSeekToSubtitle,
  rtl,
  subtitlesLength,
  isAddPossible,
  isMergePossible,
  adjacentItems,
  isSceneLastItem,
  itemOverlappedBy,
  readOnly = false,
  voiceoverItem = false,
  handleSwitchToEditor,
}) => {
  const [isEditable, setIsEditable] = useState(false);
  const [currentLength, setCurrentLength] = useState(data.text.length);
  const [textareaRows, setTextareaRows] = useState(1);
  const [isHover, setIsHover] = useState(false);

  const hasLongLine = useMemo(() => data.text.split('\n').some(item => item.length > LONG_LINE_CHARACTERS), [data.text]);

  const [isWarningHover, setIsWarningHover] = useState(false);

  const textAreaRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const containerRef = useRef(null);

  const { t } = useTranslation();


  const isFullyOverlapped = useMemo(() => checkForFullCoverage(itemOverlappedBy), [itemOverlappedBy]);

  const limits = useMemo(() => {
    const { prevItem, nextItem, sceneLimits } = adjacentItems;
    let min = sceneLimits?.start;
    let max = sceneLimits?.end;

    if (itemOverlappedBy?.length) {
      switch (itemOverlappedBy[0].coverage) {
        case VOICEOVER_COVERAGE_TYPES.START:
          if (min < itemOverlappedBy[0].end) {
            min = itemOverlappedBy[0].end;
          }
          break;
        case VOICEOVER_COVERAGE_TYPES.END:
          if (max > itemOverlappedBy[0].start) {
            max = itemOverlappedBy[0].start;
          }
          break;
        default: break;
      }
    } else {
      if (prevItem?.trimmedData) {
        min = checkForFullCoverage(prevItem?.overlappedBy) ? prevItem?.overlappedBy[prevItem?.overlappedBy.length - 1].end :
          prevItem.trimmedData.start + TIME_CORRECTION_STEP;
      }
      if (nextItem?.trimmedData) {
        max = checkForFullCoverage(nextItem?.overlappedBy) ? nextItem?.overlappedBy[0].start :
          nextItem.trimmedData.end - TIME_CORRECTION_STEP;
      }
    }
    return { min, max };
  }, [adjacentItems, data, isFullyOverlapped, itemOverlappedBy]);

  const handleEdit = e => {
    updateText(id, e.target.value, isTranslation);
  };

  const handleCardClick = e => {
    if (e.target.id?.includes('subtitle_item_card')) {
      handleSeekToSubtitle({ ...data, key: id });
    }
  };

  const handleKeyEvent = e => {
    // Reset field height
    e.target.style.height = 'inherit';
    // Get the computed styles for the element
    const computed = window.getComputedStyle(e.target);
    // Calculate the height
    const height =
      parseInt(computed.getPropertyValue('border-top-width'), 10) +
      parseInt(computed.getPropertyValue('padding-top'), 10) +
      e.target.scrollHeight +
      parseInt(computed.getPropertyValue('padding-bottom'), 10) +
      parseInt(computed.getPropertyValue('border-bottom-width'), 10);
    if (height > TEXTAREA_START_HEIGHT) {
      e.target.style.height = `${height}px`;
    } else {
      e.target.style.height = `${TEXTAREA_START_HEIGHT}px`;
    }
  };

  const handleStartUpdate = value => {
    updateStartStamp(id, value * 1000);
  };

  const handleEndUpdate = value => {
    updateEndStamp(id, value * 1000);
  };

  const updateTextareaRows = () => {
    const span = document.getElementById(`dataText-${id}`);
    if (span?.getBoundingClientRect().height > TEXTAREA_START_HEIGHT) {
      setTextareaRows(() => Math.ceil(span?.getBoundingClientRect().height / TEXTAREA_START_HEIGHT));
    }
  };

  const handleEditingFinished = e => {
    if (isEditable) {
      setIsEditable(false);
      setTimeout(() => {
        updateTextareaRows();
      }, 100);
    }
  };

  const handleStartEditing = useCallback(() => {
    if (!readOnly) {
      setIsEditable(true);
      updateTextareaRows();
    }
  }, [readOnly]);

  const handleToggleHover = (value) => () => {
    setIsHover(value);
  }

  const handleBtnClick = useCallback(() => {
    if (readOnly) {
      handleSwitchToEditor(id);
      handleSeekToSubtitle({ ...data, key: id, sceneLimits: limits });
      return;
    }
    deleteSubtitle(id);
  }, [readOnly, limits]);

  const handleKeyPress = useCallback(
    event => {
      switch (event.keyCode) {
        case ENTER_KEY_CODE:
          if (!event.shiftKey) {
            setIsEditable(false);
          }
          break;
        case ESCAPE_KEY_CODE:
          setIsEditable(false);
          break;
        default:
          break;
      }
    },
    [setIsEditable]
  );

  useEffect(() => {
    if (data?.text?.length) {
      setCurrentLength(data?.text?.length);
    }
  }, [data?.text?.length]);

  useEffect(() => {
    active &&
      !disableAutoScroll &&
      scrollContainerRef &&
      scrollContainerRef.current.scrollIntoView({ behavior: 'smooth', inline: 'nearest' });
  }, [active]);

  useEffect(() => {
    if (isEditable && textAreaRef) {
      textAreaRef.current.focus();
      textAreaRef.current.selectionStart = data.text.length;
      textAreaRef.current.selectionEnd = data.text.length;
    }
  }, [isEditable]);

  useEffect(() => {
    updateTextareaRows();
  }, []);

  useEffect(() => {
    if (isEditable) {
      document.addEventListener('keydown', handleKeyPress);
    } else {
      document.removeEventListener('keydown', handleKeyPress);
    }
  }, [isEditable]);

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative' }}
    >
      <div
        id={`subtitle_item_card_${id}`}
        className={cx(styles.wrapper, {
          [styles.active]: !isFullyOverlapped && active,
          [styles.editable]: isEditable,
          [styles.addBottom]: readOnly,
          [styles.disabled]: isFullyOverlapped,
        })}
        ref={scrollContainerRef}
        onClick={handleCardClick}
        onMouseOver={isFullyOverlapped ? handleToggleHover(true) : undefined}
        onMouseOut={isFullyOverlapped ? handleToggleHover(false) : undefined}
      >
        <div className={styles.timeInputsContainer}>
          <TimeInput
            value={data.start / 1000}
            disabled={true}
            variant="basic"
            containerClassName={cx({ [styles.startTime]: !readOnly })}
            onChange={handleStartUpdate}
            step={TIME_CORRECTION_STEP}
            min={limits.min / 1000}
            max={Math.abs((data.end - TIME_CORRECTION_STEP) / 1000)}
            showControls={!readOnly}
          />
          <span className={styles.timeSeparator}>-</span>
          <TimeInput
            value={data.end / 1000}
            disabled={true}
            variant="basic"
            onChange={handleEndUpdate}
            step={TIME_CORRECTION_STEP}
            min={data.start < TIME_CORRECTION_STEP ? TIME_CORRECTION_STEP / 1000 : (Math.min(data.start + TIME_CORRECTION_STEP, limits.max)) / 1000}
            max={limits.max / 1000}
            showControls={!readOnly}
          />
          {readOnly && voiceoverItem && <Icon src="micro" className={styles.voiceoverIcon} />}
          <div className={cx(styles.deleteSubtitleContainer, { [styles.active]: active })}>
            {!isTranslation && (
              <div className={styles.counterContainer}>
                <span className={cx(styles.counterText, styles.symbolsCount)}>
                  {currentLength}/{MAX_ITEM_LENGTH}
                </span>
                {(currentLength > MAX_ITEM_LENGTH || hasLongLine) && (
                  <>
                    <Icon
                      onMouseOver={() => setIsWarningHover(true)}
                      onMouseLeave={() => setIsWarningHover(false)}
                      src="warning"
                      className={styles.counterWarningIcon}
                    />
                    <Tooltip
                      text={t(currentLength > MAX_ITEM_LENGTH ? 'subtitles.editor.counter.warning_tooltip' : 'subtitles.editor.counter.long_line')}
                      isHover={isWarningHover}
                      classNameTooltip={styles.warningTooltip}
                      wrapperRef={scrollContainerRef}
                    />
                  </>
                )}
              </div>
            )}
            <CustomButton variant='link' onClick={handleBtnClick} className={styles.itemBtn} ariaLabel={readOnly ? t('accessibility.pencil') : t('accessibility.bin')}>
              <Icon src={readOnly ? 'pencil' : "bin"} className={styles.icon} />
            </CustomButton>
          </div>
        </div>
        {isTranslation && <div className={styles.subtitleText}>{originalData?.originalText}</div>}
        {isEditable ? (
          <DebounceInput
            element="textarea"
            debounceTimeout={200}
            onChange={handleEdit}
            className={cx(styles.editArea)}
            inputRef={textAreaRef}
            maxLength={MAX_TRANSLATED_ITEM_LENGTH}
            autoFocus
            value={data.text}
            onBlur={handleEditingFinished}
            onInput={handleKeyEvent}
            rows={textareaRows}
            direction={rtl ? 'rtl' : 'ltr'}
          />
        ) : (
          <Highlighter
            onClick={handleStartEditing}
            highlightClassName={styles.subtitleHighlighter}
            searchWords={searchValue?.trim().length > 0 ? [searchValue] : ['']}
            autoEscape
            textToHighlight={data.text || t('subtitles.editor.text.placeholder')}
            className={cx(styles.highlighterCard, { [styles.emptyText]: !data.text, [styles.readOnly]: readOnly })}
            id={`dataText-${id}`}
          />
        )}
        <p className={cx(styles.newLineCombination, { [styles.show]: isEditable })}>{t('subtitles.editor.add_new_line')}</p>
      </div>
      {!readOnly && (
        <SubtitleActions
          addSubtitle={() => addSubtitle(id)}
          mergeSubtitle={() => mergeSubtitle(id)}
          showMerge={subtitlesLength > 1 && index !== subtitlesLength - 1}
          isAddPossible={isAddPossible}
          isMergePossible={isMergePossible}
        />
      )}
      <div className={cx(styles.line, { [styles.last]: isSceneLastItem })} />
      {isFullyOverlapped ? <Tooltip
        position="middle"
        isHover={isHover} text={t('subtitles.editor.voiceover_overlapped')}
        classNameTooltip={styles.overlappedTooltip}
        wrapperRef={containerRef} /> : null}
    </div>
  );
};

export default SubtitlesItem;
